import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import {
  Building2,
  MapPin,
  Edit2,
  Save,
  X,
  Camera,
  CreditCard,
  Receipt,
  Zap,
  AlertTriangle,
  FileText,
  Mail,
  Globe,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useTranslation } from "../../i18n/hooks/useTranslation";
import { useBreadcrumbs } from "../../contexts/BreadcrumbContext";
import { useNavigation } from "../../contexts/NavigationContext";
import { AccountPaymentMethods } from "./account/AccountPaymentMethods";
import { AccountInvoices } from "./account/AccountInvoices";
import { SubscriptionManagement } from "./account/SubscriptionManagement";
import { SeatManagement } from "./account/SeatManagement";
import { UsageDashboard } from "./account/UsageDashboard";
import { ReferralManagement } from "./account/ReferralManagement";
import { AccountClosure } from "./account/AccountClosure";
import { ImageCropUploader } from "../common/ImageCropUploader";
import {
  GoogleAddressAutocomplete,
  AddressDetails,
} from "../common/GoogleAddressAutocomplete";
import { CountryAutocompleteMultiSelect } from "../common/CountryAutocompleteMultiSelect";

interface AccountData {
  _id: string;
  companyName: string;
  logo?: string;
  address: {
    formatted: string;
    street?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
  };
  poNumber?: string;
  billingEmail?: string;
  countries_enabled?: string[];
  primary_country_code?: string;
}

type AccountTab =
  | "details"
  | "payment"
  | "invoices"
  | "plan"
  | "seats"
  | "usage"
  | "referrals"
  | "close";

export const AdminAccount: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { setCustomBreadcrumbs } = useBreadcrumbs();
  const { currentPath } = useNavigation();
  const [account, setAccount] = useState<AccountData | null>(null);
  const [isLoadingAccount, setIsLoadingAccount] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [poNumber, setPoNumber] = useState("");
  const [billingEmail, setBillingEmail] = useState("");
  const [activeTab, setActiveTab] = useState<AccountTab>("details");
  const [logo, setLogo] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [addressFormatted, setAddressFormatted] = useState("");
  const [addressDetails, setAddressDetails] = useState<AddressDetails | null>(
    null,
  );
  const [countriesEnabled, setCountriesEnabled] = useState<string[]>([]);
  const [platformCountries, setPlatformCountries] = useState<{ code: string; name: string }[]>([]);
  const [showSaveNotification, setShowSaveNotification] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");

  // Check if user is account owner
  const isAccountOwner =
    user?.accountRole === "Account Owner" || user?.accountRole === "Owner";
  const isSystemAdmin = user?.role === "System Administrator";

  // Debug logging
  console.log('🔍 [AdminAccount] User role check:', JSON.stringify({
    accountRole: user?.accountRole,
    role: user?.role,
    isAccountOwner,
    isSystemAdmin
  }, null, 2));

  const showNotification = (message: string) => {
    setSaveMessage(message);
    setShowSaveNotification(true);
    setTimeout(() => setShowSaveNotification(false), 2000);
  };

  // Country code to flag emoji (e.g. NZ -> 🇳🇿)
  const countryFlag = (code: string) =>
    code
      .toUpperCase()
      .split("")
      .map((c) => String.fromCodePoint(127397 + c.charCodeAt(0)))
      .join("");

  // Helper function to convert country name to ISO 3166-1 Alpha-2 code
  const getCountryCode = (
    countryName: string | undefined,
  ): string | undefined => {
    if (!countryName) return undefined;

    const countryMap: { [key: string]: string } = {
      "new zealand": "nz",
      australia: "au",
      "united states": "us",
      "united states of america": "us",
      usa: "us",
      "united kingdom": "gb",
      uk: "gb",
      canada: "ca",
      "south africa": "za",
      ireland: "ie",
      singapore: "sg",
      malaysia: "my",
      philippines: "ph",
      indonesia: "id",
      thailand: "th",
      vietnam: "vn",
      japan: "jp",
      "south korea": "kr",
      china: "cn",
      india: "in",
      france: "fr",
      germany: "de",
      italy: "it",
      spain: "es",
      netherlands: "nl",
      belgium: "be",
      switzerland: "ch",
      austria: "at",
      sweden: "se",
      norway: "no",
      denmark: "dk",
      finland: "fi",
      poland: "pl",
      portugal: "pt",
      brazil: "br",
      mexico: "mx",
      argentina: "ar",
      chile: "cl",
      colombia: "co",
      peru: "pe",
      uae: "ae",
      "united arab emirates": "ae",
      "saudi arabia": "sa",
      israel: "il",
      turkey: "tr",
      egypt: "eg",
      nigeria: "ng",
      kenya: "ke",
    };

    const normalized = countryName.toLowerCase().trim();
    return countryMap[normalized];
  };

  // Read URL parameters to set active sub-tab
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const subTabParam = urlParams.get('subTab') as AccountTab;
    const accountTabParam = urlParams.get('accountTab') as AccountTab;
    
    // Check both 'subTab' and 'accountTab' parameters for backward compatibility
    const tabFromUrl = subTabParam || accountTabParam;
    
    if (tabFromUrl && ['details', 'plan', 'seats', 'usage', 'referrals', 'payment', 'invoices', 'close'].includes(tabFromUrl)) {
      setActiveTab(tabFromUrl);
    }
  }, [currentPath]);

  // Redirect non-owners away from owner-only tabs
  useEffect(() => {
    const ownerOnlyTabs: AccountTab[] = [
      "plan",
      "seats",
      "payment",
      "invoices",
      "referrals",
      "close",
    ];

    if (
      !isAccountOwner &&
      !isSystemAdmin &&
      ownerOnlyTabs.includes(activeTab)
    ) {
      // Redirect to details tab if non-owner tries to access owner-only tab
      setActiveTab("details");
    }
  }, [activeTab, isAccountOwner, isSystemAdmin]);

  useEffect(() => {
    const fetchAccount = async () => {
      if (!user?.currentAccountId) {
        setIsLoadingAccount(false);
        return;
      }

      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          `${process.env.REACT_APP_API_URL || "http://localhost:5000"}/api/accounts/${user.currentAccountId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (response.ok) {
          const data = await response.json();
          setAccount(data.account);
          setLogo(data.account.logo || null);
          // Initialize edit fields
          setCompanyName(data.account.companyName);
          setPoNumber(data.account.poNumber || "");
          setBillingEmail(data.account.billingEmail || "");
          setCountriesEnabled(data.account.countries_enabled || []);
          const addr = data.account.address;
          setAddressFormatted(addr?.formatted || "");
          if (addr) {
            setAddressDetails({
              formatted: addr.formatted || "",
              streetNumber: (addr as any).streetNumber,
              streetName: (addr as any).streetName,
              street: addr.street,
              city: addr.city,
              state: addr.state,
              postalCode: addr.postalCode,
              country: addr.country,
              lat: (addr as any).lat,
              lng: (addr as any).lng,
            });
          }
        }
      } catch (error) {
        console.error("Failed to fetch account:", error);
      } finally {
        setIsLoadingAccount(false);
      }
    };

    fetchAccount();
  }, [user?.currentAccountId]);

  // Fetch platform countries for account admin
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(
          `${process.env.REACT_APP_API_URL || "http://localhost:5000"}/api/construction/countries`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await res.json();
        if (data.success && data.data) {
          setPlatformCountries(data.data);
        }
      } catch (err) {
        console.error("Failed to fetch countries:", err);
      }
    };
    fetchCountries();
  }, []);

  const handleAddressSelect = (details: AddressDetails) => {
    setAddressFormatted(details.formatted);
    setAddressDetails(details);
  };

  // Update URL when activeTab changes (but only update subTab parameter, not main tab)
  useEffect(() => {
    const url = new URL(window.location.href);
    // Only update if we're on the account tab
    if (url.searchParams.get('tab') === 'account') {
      if (activeTab === 'details') {
        // Remove subTab parameter for default tab
        url.searchParams.delete('subTab');
        url.searchParams.delete('accountTab');
      } else {
        // Set subTab parameter for other tabs
        url.searchParams.set('subTab', activeTab);
        url.searchParams.delete('accountTab'); // Clean up old parameter
      }
      window.history.replaceState({}, '', url.toString());
    }
  }, [activeTab]);

  // Track if this component is mounted to prevent stale updates
  const isMountedRef = useRef(true);

  // Update breadcrumbs when tab changes - use useLayoutEffect for synchronous updates
  useLayoutEffect(() => {
    isMountedRef.current = true;

    // Always clear first to prevent accumulation
    setCustomBreadcrumbs(null);

    const baseBreadcrumbs = [
      { label: t("navigation.home"), path: "/", icon: "Home" },
      {
        label: t("navigation.admin"),
        path: "/admin?tab=account",
        icon: "Settings",
      },
      {
        label: t("navigation.account"),
        path: "/admin?tab=account",
        icon: "Building2",
      },
    ];

    const tabLabels: Record<AccountTab, string> = {
      details: t("admin.accountDetails"),
      plan: t("admin.subscriptionPlan"),
      seats: t("admin.seatManagement"),
      usage: t("admin.aiUsage"),
      referrals: t("admin.referrals"),
      payment: t("admin.paymentMethods"),
      invoices: t("admin.billingInvoices"),
      close: t("account.close.title"),
    };

    setCustomBreadcrumbs([
      ...baseBreadcrumbs,
      {
        label: tabLabels[activeTab] || "",
        path: "/admin?tab=account",
        icon: "Building2",
      },
    ]);

    // Cleanup: mark as unmounted
    return () => {
      isMountedRef.current = false;
    };
  }, [activeTab, t, setCustomBreadcrumbs]);

  const handleSave = async () => {
    if (!user?.currentAccountId) return;

    setIsSaving(true);
    try {
      const token = localStorage.getItem("token");

      const response = await fetch(
        `${process.env.REACT_APP_API_URL || "http://localhost:5000"}/api/accounts/${user.currentAccountId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            companyName,
            poNumber: poNumber.trim() === "" ? "" : poNumber.trim(),
            billingEmail: billingEmail.trim() === "" ? "" : billingEmail.trim(),
            countries_enabled: countriesEnabled,
            address: addressDetails
              ? {
                  formatted: addressDetails.formatted,
                  streetNumber: addressDetails.streetNumber,
                  streetName: addressDetails.streetName,
                  street: addressDetails.street,
                  city: addressDetails.city,
                  state: addressDetails.state,
                  postalCode: addressDetails.postalCode,
                  country: addressDetails.country,
                  lat: addressDetails.lat,
                  lng: addressDetails.lng,
                }
              : {
                  formatted: addressFormatted,
                },
          }),
        },
      );

      const data = await response.json();
      if (data.success) {
        setAccount(data.account);
        setCountriesEnabled(data.account.countries_enabled || []);
        setIsEditing(false);
        showNotification(t("admin.accountSaved") || "Account saved");
      } else {
        throw new Error(data.message || "Failed to update account");
      }
    } catch (error) {
      console.error("Failed to update account:", error);
      showNotification(
        t("admin.accountSaveFailed") || "Failed to save account",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (account) {
      setCompanyName(account.companyName);
      setPoNumber(account.poNumber || "");
      setBillingEmail(account.billingEmail || "");
      setCountriesEnabled(account.countries_enabled || []);
      const addr = account.address;
      setAddressFormatted(addr.formatted || "");
      if (addr) {
        setAddressDetails({
          formatted: addr.formatted || "",
          streetNumber: (addr as any).streetNumber,
          streetName: (addr as any).streetName,
          street: addr.street,
          city: addr.city,
          state: addr.state,
          postalCode: addr.postalCode,
          country: addr.country,
          lat: (addr as any).lat,
          lng: (addr as any).lng,
        });
      } else {
        setAddressDetails(null);
      }
    }
    setIsEditing(false);
  };

  const handleLogoClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      alert("Please select an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert("Image must be less than 5MB");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setSelectedFile(event.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleLogoCropped = async (croppedBlob: Blob, previewUrl: string) => {
    if (!user?.currentAccountId) return;

    setUploadingLogo(true);
    setSelectedFile(null);

    try {
      // Set preview immediately
      setLogo(previewUrl);

      // Upload to server
      const formData = new FormData();
      formData.append("logo", croppedBlob, "logo.jpg");

      const token = localStorage.getItem("token");
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || "http://localhost:5000"}/api/accounts/${user.currentAccountId}/logo`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        },
      );

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.message || "Failed to upload logo");
      }

      // Update with server-returned logo
      setLogo(data.account.logo);
      setAccount((prev) =>
        prev ? { ...prev, logo: data.account.logo } : null,
      );
    } catch (error) {
      console.error("Failed to upload logo:", error);
      // Revert to previous logo on error
      setLogo(account?.logo || null);
    } finally {
      setUploadingLogo(false);
    }
  };

  // Define all tabs with ownership requirements
  const allTabs = [
    {
      id: "details" as AccountTab,
      label: t("admin.accountDetails"),
      icon: Building2,
      ownerOnly: false,
    },
    {
      id: "plan" as AccountTab,
      label: t("admin.subscriptionPlan"),
      icon: Zap,
      ownerOnly: true,
    },
    {
      id: "seats" as AccountTab,
      label: t("admin.seatManagement"),
      icon: CreditCard,
      ownerOnly: true,
    },
    {
      id: "usage" as AccountTab,
      label: t("admin.aiUsage"),
      icon: Receipt,
      ownerOnly: false,
    },
    {
      id: "referrals" as AccountTab,
      label: t("admin.referrals"),
      icon: Zap,
      ownerOnly: true,
    },
    {
      id: "payment" as AccountTab,
      label: t("admin.paymentMethods"),
      icon: CreditCard,
      ownerOnly: true,
    },
    {
      id: "invoices" as AccountTab,
      label: t("admin.billingInvoices"),
      icon: Receipt,
      ownerOnly: true,
    },
    {
      id: "close" as AccountTab,
      label: t("account.close.title"),
      icon: AlertTriangle,
      ownerOnly: true,
    },
  ];

  // Filter tabs based on user role
  const tabs = allTabs.filter((tab) => {
    // System admins see all tabs
    if (isSystemAdmin) return true;
    // Account owners see all tabs
    if (isAccountOwner) return true;
    // Regular users only see non-owner tabs
    return !tab.ownerOnly;
  });

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {t("admin.account")}
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            {t("admin.accountDescription")}
          </p>
        </div>
        {account &&
          user?.role !== "System Administrator" &&
          activeTab === "details" &&
          (!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
            >
              <Edit2 className="w-4 h-4" />
              <span>{t("admin.editAccount")}</span>
            </button>
          ) : (
            <div className="flex space-x-2">
              <button
                onClick={handleCancel}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                <X className="w-4 h-4" />
                <span>{t("common.cancel")}</span>
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex items-center space-x-2 px-4 py-2 bg-success-500 hover:bg-success-600 disabled:bg-gray-400 text-white rounded-lg transition-colors"
              >
                <Save className="w-4 h-4" />
                <span>{isSaving ? t("common.saving") : t("common.save")}</span>
              </button>
            </div>
          ))}
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200 dark:border-gray-600">
        <nav className="flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{ outline: "none", boxShadow: "none" }}
              className={`flex items-center space-x-2 pb-4 px-1 border-b-2 font-medium text-sm transition-colors [&:focus]:outline-none [&:focus]:ring-0 [&:focus]:shadow-none [&:active]:outline-none ${
                activeTab === tab.id
                  ? "border-primary-500 text-primary-600 dark:text-primary-400"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300"
              }`}
            >
              <tab.icon className="w-5 h-5" />
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === "details" && (
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 border border-gray-200 dark:border-gray-600">
          {isLoadingAccount ? (
            <div className="text-center text-gray-500 dark:text-gray-400 py-8">
              {t("admin.loadingAccountInformation")}
            </div>
          ) : account ? (
            <>
              {/* Logo Section */}
              <div className="mb-6 flex items-start space-x-6">
                <div className="flex-shrink-0">
                  <div className="relative group">
                    <div className="w-24 h-24 rounded-lg overflow-hidden bg-gray-200 dark:bg-gray-600 flex items-center justify-center relative border-2 border-gray-300 dark:border-gray-500">
                      {logo ? (
                        <img
                          src={logo}
                          alt="Company Logo"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <Building2 className="w-12 h-12 text-gray-400" />
                      )}

                      {/* Overlay on hover - logo always editable */}
                      <div
                        onClick={handleLogoClick}
                        className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                      >
                        <div className="text-white text-center">
                          {uploadingLogo ? (
                            <div className="w-6 h-6 border-4 border-white border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <Camera className="w-6 h-6" />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    {t("admin.companyLogo")}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500">
                    {t("admin.companyLogoHint")}
                  </p>
                </div>
              </div>

              {isEditing ? (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <div className="flex items-center space-x-2">
                        <Building2 className="w-5 h-5" />
                        <span>{t("admin.companyName")}</span>
                      </div>
                    </label>
                    <input
                      type="text"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder={t("admin.companyNamePlaceholder")}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <div className="flex items-center space-x-2">
                        <Globe className="w-5 h-5" />
                        <span>Countries we work in</span>
                      </div>
                    </label>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                      Type to search and select countries. These will appear in Create Project.
                    </p>
                    <CountryAutocompleteMultiSelect
                      countries={platformCountries}
                      selected={countriesEnabled}
                      onChange={setCountriesEnabled}
                      placeholder="Type country name or code..."
                      disabled={!isEditing}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <div className="flex items-center space-x-2">
                        <MapPin className="w-5 h-5" />
                        <span>{t("admin.address")}</span>
                      </div>
                    </label>
                    <GoogleAddressAutocomplete
                      value={addressFormatted}
                      onChange={setAddressFormatted}
                      onPlaceSelect={handleAddressSelect}
                      placeholder={t("admin.streetAddressPlaceholder")}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      id="address-autocomplete"
                      name="address"
                      disabled={!isEditing}
                      countryBias={
                        account?.address?.country
                          ? getCountryCode(account.address.country)
                          : undefined
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <div className="flex items-center space-x-2">
                        <FileText className="w-5 h-5" />
                        <span>{t("admin.poNumber")}</span>
                      </div>
                    </label>
                    <input
                      type="text"
                      value={poNumber}
                      onChange={(e) => setPoNumber(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder={t("admin.poNumberPlaceholder")}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <div className="flex items-center space-x-2">
                        <Mail className="w-5 h-5" />
                        <span>{t("admin.billingEmail")}</span>
                      </div>
                    </label>
                    <input
                      type="email"
                      value={billingEmail}
                      onChange={(e) => setBillingEmail(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder={t("admin.billingEmailPlaceholder")}
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <div className="flex items-center space-x-2">
                        <Building2 className="w-5 h-5" />
                        <span>{t("admin.companyName")}</span>
                      </div>
                    </label>
                    <div className="text-xl text-gray-900 dark:text-gray-100">
                      {account.companyName}
                    </div>
                  </div>
                  {(account.countries_enabled?.length ?? 0) > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        <div className="flex items-center space-x-2">
                          <Globe className="w-5 h-5" />
                          <span>Countries we work in</span>
                        </div>
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {(account.countries_enabled ?? []).map((code) => {
                          const c = platformCountries.find((x) => x.code === code);
                          return (
                            <span
                              key={code}
                              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-600 text-gray-900 dark:text-gray-100"
                            >
                              <span className="text-lg">{countryFlag(code)}</span>
                              <span>{c?.name || code}</span>
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <div className="flex items-center space-x-2">
                        <MapPin className="w-5 h-5" />
                        <span>{t("admin.address")}</span>
                      </div>
                    </label>
                    <div className="text-gray-900 dark:text-gray-100">
                      {account.address.formatted}
                    </div>
                    {account.address.city && account.address.state && (
                      <div className="text-gray-600 dark:text-gray-400 mt-1">
                        {account.address.city}, {account.address.state}
                        {account.address.postalCode &&
                          ` ${account.address.postalCode}`}
                      </div>
                    )}
                  </div>
                  {account.poNumber && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        <div className="flex items-center space-x-2">
                          <FileText className="w-5 h-5" />
                          <span>{t("admin.poNumber")}</span>
                        </div>
                      </label>
                      <div className="text-gray-900 dark:text-gray-100">
                        {account.poNumber}
                      </div>
                    </div>
                  )}
                  {account.billingEmail && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        <div className="flex items-center space-x-2">
                          <Mail className="w-5 h-5" />
                          <span>{t("admin.billingEmail")}</span>
                        </div>
                      </label>
                      <div className="text-gray-900 dark:text-gray-100">
                        {account.billingEmail}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : user?.role === "System Administrator" ? (
            <div className="text-center text-gray-500 dark:text-gray-400 py-8">
              System Administrators are not associated with a specific account.
            </div>
          ) : (
            <div className="text-center text-gray-500 dark:text-gray-400 py-8">
              No account information available.
            </div>
          )}
        </div>
      )}

      {/* Payment Methods Tab - Owner Only */}
      {activeTab === "payment" &&
        (isAccountOwner || isSystemAdmin ? (
          <AccountPaymentMethods />
        ) : (
          <div className="bg-yellow-50 dark:bg-gray-700 dark:border dark:border-warning-800/50 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-warning-600 dark:text-warning-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-warning-900 dark:text-warning-100">
                  {t("admin.ownerOnlyAccess") ||
                    "Account Owner Access Required"}
                </h3>
                <p className="text-sm text-warning-800 dark:text-warning-300 mt-1">
                  {t("admin.ownerOnlyMessage") ||
                    "Only account owners can manage payment methods. Please contact your account owner for assistance."}
                </p>
              </div>
            </div>
          </div>
        ))}

      {/* Invoices Tab - Owner Only */}
      {activeTab === "invoices" &&
        (isAccountOwner || isSystemAdmin ? (
          <AccountInvoices />
        ) : (
          <div className="bg-yellow-50 dark:bg-gray-700 dark:border dark:border-warning-800/50 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-warning-600 dark:text-warning-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-warning-900 dark:text-warning-100">
                  {t("admin.ownerOnlyAccess") ||
                    "Account Owner Access Required"}
                </h3>
                <p className="text-sm text-warning-800 dark:text-warning-300 mt-1">
                  {t("admin.ownerOnlyMessage") ||
                    "Only account owners can view invoices. Please contact your account owner for assistance."}
                </p>
              </div>
            </div>
          </div>
        ))}

      {/* Subscription Plan Tab - Owner Only */}
      {activeTab === "plan" &&
        (isAccountOwner || isSystemAdmin ? (
          <SubscriptionManagement />
        ) : (
          <div className="bg-yellow-50 dark:bg-gray-700 dark:border dark:border-warning-800/50 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-warning-600 dark:text-warning-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-warning-900 dark:text-warning-100">
                  {t("admin.ownerOnlyAccess") ||
                    "Account Owner Access Required"}
                </h3>
                <p className="text-sm text-warning-800 dark:text-warning-300 mt-1">
                  {t("admin.ownerOnlyMessage") ||
                    "Only account owners can manage subscriptions. Please contact your account owner for assistance."}
                </p>
              </div>
            </div>
          </div>
        ))}

      {/* Seat Management Tab - Owner Only */}
      {activeTab === "seats" &&
        (isAccountOwner || isSystemAdmin ? (
          <SeatManagement />
        ) : (
          <div className="bg-yellow-50 dark:bg-gray-700 dark:border dark:border-warning-800/50 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-warning-600 dark:text-warning-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-warning-900 dark:text-warning-100">
                  {t("admin.ownerOnlyAccess") ||
                    "Account Owner Access Required"}
                </h3>
                <p className="text-sm text-warning-800 dark:text-warning-300 mt-1">
                  {t("admin.ownerOnlyMessage") ||
                    "Only account owners can manage seats. Please contact your account owner for assistance."}
                </p>
              </div>
            </div>
          </div>
        ))}

      {/* AI Usage Tab - Available to all */}
      {activeTab === "usage" && <UsageDashboard />}

      {/* Referrals Tab - Owner Only */}
      {activeTab === "referrals" &&
        (isAccountOwner || isSystemAdmin ? (
          <ReferralManagement />
        ) : (
          <div className="bg-yellow-50 dark:bg-gray-700 dark:border dark:border-warning-800/50 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-warning-600 dark:text-warning-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-warning-900 dark:text-warning-100">
                  {t("admin.ownerOnlyAccess") ||
                    "Account Owner Access Required"}
                </h3>
                <p className="text-sm text-warning-800 dark:text-warning-300 mt-1">
                  {t("admin.ownerOnlyMessage") ||
                    "Only account owners can manage referrals. Please contact your account owner for assistance."}
                </p>
              </div>
            </div>
          </div>
        ))}

      {/* Account Closure Tab - Owner Only */}
      {activeTab === "close" &&
        user?.currentAccountId &&
        (isAccountOwner || isSystemAdmin ? (
          <AccountClosure accountId={user.currentAccountId} />
        ) : (
          <div className="bg-yellow-50 dark:bg-gray-700 dark:border dark:border-warning-800/50 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-warning-600 dark:text-warning-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-warning-900 dark:text-warning-100">
                  {t("admin.ownerOnlyAccess") ||
                    "Account Owner Access Required"}
                </h3>
                <p className="text-sm text-warning-800 dark:text-warning-300 mt-1">
                  {t("admin.ownerOnlyMessage") ||
                    "Only account owners can close accounts. Please contact your account owner for assistance."}
                </p>
              </div>
            </div>
          </div>
        ))}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Logo Crop Uploader Modal */}
      {selectedFile && (
        <ImageCropUploader
          selectedImage={selectedFile}
          onImageCropped={handleLogoCropped}
          onCancel={() => setSelectedFile(null)}
          aspectRatio={1}
          title="Crop Company Logo"
          maxSizeKB={5120}
        />
      )}

      {/* Save Notification */}
      {showSaveNotification && (
        <div
          className={`fixed bottom-4 right-4 ${saveMessage.toLowerCase().includes("failed") || saveMessage.toLowerCase().includes("invalid") || saveMessage.toLowerCase().includes("error") ? "bg-error-500" : "bg-success-500"} text-white px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2 animate-fade-in z-50`}
        >
          {saveMessage.toLowerCase().includes("failed") ||
          saveMessage.toLowerCase().includes("invalid") ||
          saveMessage.toLowerCase().includes("error") ? (
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          ) : (
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          )}
          <span>{saveMessage}</span>
        </div>
      )}
    </div>
  );
};
